<div class="view-container">
<div class="prescription-flow form-ui" [ngClass]="{'container': (isSmallScreen | async)}">
  <nav class="order-steps" aria-label="Prescription progress">
    @for (step of visibleFlowSteps; track step.label; let i = $index) {
      <button
        type="button"
        class="order-steps__item"
        [class.order-steps__item--active]="currentStepIndex === i"
        [class.order-steps__item--completed]="currentStepIndex > i"
        [disabled]="i > currentStepIndex"
        (click)="goToStep(i)"
      >
        <span class="order-steps__number">{{ i + 1 }}</span>
        <span class="order-steps__label">{{ step.label }}</span>
      </button>
    }
  </nav>

  <mat-card class="prescription-flow__card">
    <mat-card-title>
    New prescription {{ individualFirstName && !companyName ? " - " + individualFirstName + (individualLastName ? " " + individualLastName : "") : ""}} {{!individualFirstName && companyName ? " - " + companyName : ""}}</mat-card-title>

    <mat-stepper class="prescription-flow__stepper" (selectionChange)="stepChanged($event)" linear #stepper
      [orientation]="(stepperOrientation | async)!">
      <mat-step [stepControl]="selectEntityTypeForm" [editable]="isEditable">
        <ng-template matStepLabel>Who is this for?</ng-template>
        <form class="prescription-step" [formGroup]="selectEntityTypeForm">
          <div class="prescription-step__header">
            <div class="prescription-step__title-row">
              <div class="prescription-step__icon" aria-hidden="true">
                <mat-icon>person</mat-icon>
              </div>
              <div>
                <h2 class="prescription-step__title">Who is this for?</h2>
                <p class="prescription-step__subtitle">
                  Choose whether this prescription is for an individual or a company.
                </p>
              </div>
            </div>
          </div>

          <div class="prescription-step__fields">
            <mat-form-field appearance="outline" class="prescription-step__select" subscriptSizing="dynamic">
              <mat-label>Who is this prescription for?</mat-label>
              <mat-select required data-testid="entity-type" [disabled]="isLoading" #chosenValue
                formControlName="entityType">
                @for (entityType of entityTypes; track entityType) {
                  <mat-option [value]="entityType">
                    {{entityType}}
                  </mat-option>
                }
              </mat-select>
              @if (selectEntityTypeForm.get('entityType')?.invalid) {
                <mat-error>
                  Choose from provided options
                </mat-error>
              }
            </mat-form-field>
          </div>

          <div class="prescription-step__actions">
            <button data-testid="next-step" [disabled]="!selectEntityTypeForm.valid"
              color="primary" mat-raised-button matStepperNext>
              Next<mat-icon iconPositionEnd>chevron_right</mat-icon>
            </button>
          </div>
        </form>
      </mat-step>

      @if (selectEntityTypeForm.value.entityType===EntityType.Company) {
        <mat-step [stepControl]="companyDetailsForm" [editable]="isEditable">
          <ng-template matStepLabel>Company details</ng-template>
          <form class="mt-4" [formGroup]="companyDetailsForm">
            <div class="row">
              <div class="col-md-6">
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Company name</mat-label>
                    <input required data-testid="company-name" readonly (click)="openSearchCompanyDialog()" [disabled]="isLoading" type="text" matInput formControlName="companyName">
                    @if (!selectedCompany) {
                      <button type="button"
                        (click)="openSearchCompanyDialog()" mat-icon-button matSuffix
                        [attr.aria-label]="'Search for a company'">
                        <mat-icon>search</mat-icon>
                      </button>
                    }
                    @if (selectedCompany) {
                      <button type="button" (click)="deselectCompany()"
                        mat-icon-button matSuffix [attr.aria-label]="'Remove customer'">
                        <mat-icon>cancel</mat-icon>
                      </button>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Phone number</mat-label>
                    <input required data-testid="company-phone-number" formControlName="phoneNumber"
                      matInput>
                    @if (companyDetailsForm.get('phoneNumber')?.invalid) {
                      <mat-error>
                        Phone number is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Alternative phone number</mat-label>
                    <input data-testid="company-phone-number-alt" formControlName="phoneNumberAlt"
                      matInput>
                    @if (companyDetailsForm.get('phoneNumberAlt')?.invalid) {
                      <mat-error>
                        Alternative phone number is not valid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Email address</mat-label>
                    <input required data-testid="company-email" formControlName="email" matInput>
                    @if (companyDetailsForm.get('email')?.invalid) {
                      <mat-error>
                        Email address is required
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
              </div>
              <div class="col-md-6">
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Alternative email address</mat-label>
                    <input data-testid="company-email-alt" formControlName="emailAlt" matInput>
                    @if (companyDetailsForm.get('emailAlt')?.invalid) {
                      <mat-error>
                        Alternative email address is not valid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Practice number</mat-label>
                    <input data-testid="company-practice-number" formControlName="practiceNumber"
                      matInput>
                    @if (companyDetailsForm.get('practiceNumber')?.invalid) {
                      <mat-error>
                        Practice number is required
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>VAT registration number</mat-label>
                    <input data-testid="company-vat-reg-number"
                      formControlName="vatRegistrationNumber" matInput>
                    @if (companyDetailsForm.get('vatRegistrationNumber')?.invalid) {
                      <mat-error>
                        VAT registration number is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Type</mat-label>
                    <mat-select required data-testid="company-type"
                      formControlName="typeOfBusiness">
                      <mat-option [value]="undefined">Not Applicable</mat-option>
                      @for (type of companyTypes; track type) {
                        <mat-option [value]="type.id">
                          {{type.type}}
                        </mat-option>
                      }
                    </mat-select>
                    @if (companyDetailsForm.get('typeOfBusiness')?.invalid) {
                      <mat-error>
                        Select company type
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
              </div>
            </div>
            <div class="my-2">
              <p>
                <mat-checkbox color="primary" class="me-2" [checked]="displayPrescriberDetailsForm"
                  (change)="togglePrescriberDetailsStep($event)">
                </mat-checkbox>
                <span>Provide prescriber details</span>
              </p>
            </div>
            @if (showDistributor) {
              <div class="my-2">
                <div class="py-2">
                  <mat-divider></mat-divider>
                </div>
                <!-- <p>
                <button *ngIf="!selectedDistributor" mat-stroked-button
                  (click)="openSearchDistributorDialog()">
                  Select distributor
                </button>
              </p> -->
              @if (selectedDistributor) {
                <div class="details mt-2">
                  <div>
                    <button mat-stroked-button (click)="deselectDistributor()" class="me-2 my-2">
                      <mat-icon>cancel</mat-icon> Remove
                    </button>
                    <table>
                      <tr>
                        <th>
                          Name
                        </th>
                        <td>
                          {{ selectedDistributor.name ? selectedDistributor.name : "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>
                          Alternative email address
                        </th>
                        <td>
                          {{ selectedDistributor.email_alt ? selectedDistributor.email_alt : "-"
                          }}
                        </td>
                      </tr>
                      <tr>
                        <th>
                          Phone number
                        </th>
                        <td>
                          {{ selectedDistributor.phone_number ? selectedDistributor.phone_number :
                          "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>
                          Email
                        </th>
                        <td>
                          {{ selectedDistributor.email ? selectedDistributor.email : "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>
                          Alternative phone number
                        </th>
                        <td>
                          {{ selectedDistributor.phone_number_alt ?
                          selectedDistributor.phone_number_alt : "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>
                          Practice number
                        </th>
                        <td>
                          {{ selectedDistributor.practice_number ?
                          selectedDistributor.practice_number : "-" }}
                        </td>
                      </tr>
                      <tr>
                        <th>
                          VAT registration number
                        </th>
                        <td>
                          {{ selectedDistributor.vat_registration_number ?
                          selectedDistributor.vat_registration_number : "-" }}
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              }
            </div>
          }
          @if (showDistributor && !selectedDistributor) {
            <p class="distributor-required-hint" role="alert">
              Please add a distributor to this company before you continue.
            </p>
          }
          <div class='text-end'>
            <button class='mx-1' data-testid="previous-step" color="primary" mat-raised-button
              matStepperPrevious>
              <mat-icon>chevron_left</mat-icon> Back
            </button>
            <button [disabled]="!canProceedFromCompanyStep()" data-testid="next-step" class='mx-1'
              color="primary" mat-raised-button matStepperNext>
               Next<mat-icon iconPositionEnd>chevron_right</mat-icon>
            </button>
          </div>
        </form>
      </mat-step>
      @if (displayPrescriberDetailsForm) {
        <mat-step [stepControl]="prescriberDetailsForm"
          [editable]="isEditable">
          <ng-template matStepLabel>Prescriber details</ng-template>
          <form class="mt-4" [formGroup]="prescriberDetailsForm">
            <div class="row">
              <div class="col-md-6">
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>First name</mat-label>
                    <input required readonly (click)="openSearchPrescriberDialog()" data-testid="individual-first-name" formControlName="firstName"
                      matInput>
                    @if (prescriberDetailsForm.get('firstName')?.invalid) {
                      <mat-error>
                        First name is invalid
                      </mat-error>
                    }
                    @if (!selectedPrescriber) {
                      <button type="button"
                        (click)="openSearchPrescriberDialog()" mat-icon-button matSuffix
                        [attr.aria-label]="'Search for a prescriber'">
                        <mat-icon>search</mat-icon>
                      </button>
                    }
                    @if (selectedPrescriber) {
                      <button type="button" (click)="deselectPrescriber()"
                        mat-icon-button matSuffix [attr.aria-label]="'Remove prescriber'">
                        <mat-icon>cancel</mat-icon>
                      </button>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Last name</mat-label>
                    <input required data-testid="individual-last-name" formControlName="lastName"
                      matInput>
                    @if (prescriberDetailsForm.get('lastName')?.invalid) {
                      <mat-error>
                        Last name is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Title</mat-label>
                    <mat-select required data-testid="individual-title" formControlName="title">
                      <mat-option value="Mr">Mr</mat-option>
                      <mat-option value="Mrs">Mrs</mat-option>
                      <mat-option value="Miss">Miss</mat-option>
                      <mat-option value="Prof">Prof</mat-option>
                      <mat-option value="Dr">Dr</mat-option>
                    </mat-select>
                    @if (prescriberDetailsForm.get('title')?.invalid) {
                      <mat-error>
                        Title is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Initials</mat-label>
                    <input required data-testid="individual-initials" formControlName="initials"
                      matInput>
                    @if (prescriberDetailsForm.get('initials')?.invalid) {
                      <mat-error>
                        Initials are invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Gender</mat-label>
                    <mat-select required data-testid="individual-gender" formControlName="gender">
                      <mat-option value="male">Male</mat-option>
                      <mat-option value="female">Female</mat-option>
                      <mat-option value="other">Other</mat-option>
                    </mat-select>
                    @if (prescriberDetailsForm.get('gender')?.invalid) {
                      <mat-error>
                        Gender is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Date of birth</mat-label>
                    <input required data-testid="individual-date-of-birth"
                      formControlName="dateOfBirth" matInput [matDatepicker]="picker3">
                    <mat-datepicker-toggle matSuffix [for]="picker3"></mat-datepicker-toggle>
                    <mat-datepicker #picker3></mat-datepicker>
                    @if (prescriberDetailsForm.get('date')?.invalid) {
                      <mat-error>
                        Date of birth is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
              </div>
              <div class="col-md-6">
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Email address</mat-label>
                    <input required data-testid="individual-email" formControlName="email" matInput>
                    @if (prescriberDetailsForm.get('email')?.invalid) {
                      <mat-error>
                        Email address is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Alternative email address</mat-label>
                    <input data-testid="individual-alt-email" formControlName="emailAlt" matInput>
                    @if (prescriberDetailsForm.get('emailAlt')?.invalid) {
                      <mat-error>
                        Alternative email address is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Number of dependants</mat-label>
                    <input data-testid="individual-number-of-dependants" type="number" min="0"
                      formControlName="numberOfDependants" matInput>
                    @if (prescriberDetailsForm.get('numberOfDependants')?.invalid) {
                      <mat-error>
                        Number of dependants is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Individual type</mat-label>
                    <mat-select required data-testid="individual-type" [disabled]="isLoading"
                      #chosenValue formControlName="individualType">
                      @for (type of individualTypes; track type) {
                        <mat-option [value]="type.id">
                          {{type.type}}
                        </mat-option>
                      }
                    </mat-select>
                    @if (prescriberDetailsForm.get('individualType')?.invalid) {
                      <mat-error>
                        Individual type is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field required appearance="outline">
                    <mat-label>Phone number</mat-label>
                    <input required data-testid="individual-phone-number"
                      formControlName="phoneNumber" matInput>
                    @if (prescriberDetailsForm.get('phoneNumber')?.invalid) {
                      <mat-error>
                        Phone number is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
                <p>
                  <mat-form-field appearance="outline">
                    <mat-label>Alternative phone number</mat-label>
                    <input data-testid="individual-alt-phone-number"
                      formControlName="phoneNumberAlt" matInput>
                    @if (prescriberDetailsForm.get('phoneNumberAlt')?.invalid) {
                      <mat-error>
                        Alternative phone number is invalid
                      </mat-error>
                    }
                  </mat-form-field>
                </p>
              </div>
            </div>
            <div class="text-end">
              <button class="mx-1" data-testid="previoous-step" color="primary" mat-raised-button
                matStepperPrevious>
                <mat-icon>chevron_left</mat-icon> Back
              </button>
              <button data-testid="next-step" [disabled]="!prescriberDetailsForm.valid" class="mx-1"
                color="primary" mat-raised-button matStepperNext>
                Next<mat-icon iconPositionEnd>chevron_right</mat-icon>
              </button>
            </div>
          </form>
        </mat-step>
      }
    }

    @if (selectEntityTypeForm.value.entityType===EntityType.Individual) {
      <mat-step [stepControl]="individualDetailsForm" [editable]="isEditable">
        <ng-template matStepLabel>Individual details</ng-template>
        <form class="mt-4" [formGroup]="individualDetailsForm">
          <div class="row">
            <div class="col-md-6">
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>First name</mat-label>
                  <input required readonly (click)="openSearchIndividualDialog()" data-testid="individual-first-name" formControlName="firstName"
                    matInput>
                  @if (individualDetailsForm.get('firstName')?.invalid) {
                    <mat-error>
                      First name is invalid
                    </mat-error>
                  }
                  @if (!selectedIndividual) {
                    <button type="button"
                      (click)="openSearchIndividualDialog()" mat-icon-button matSuffix
                      [attr.aria-label]="'Search individual'">
                      <mat-icon>search</mat-icon>
                    </button>
                  }
                  @if (selectedIndividual) {
                    <button type="button" (click)="deselectIndividual()"
                      mat-icon-button matSuffix [attr.aria-label]="'Remove customer'">
                      <mat-icon>cancel</mat-icon>
                    </button>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Last name</mat-label>
                  <input required data-testid="individual-last-name" formControlName="lastName"
                    matInput>
                  @if (individualDetailsForm.get('lastName')?.invalid) {
                    <mat-error>
                      Last name is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Title</mat-label>
                  <mat-select required data-testid="individual-title" formControlName="title">
                    <mat-option value="Mr">Mr</mat-option>
                    <mat-option value="Mrs">Mrs</mat-option>
                    <mat-option value="Miss">Miss</mat-option>
                    <mat-option value="Prof">Prof</mat-option>
                    <mat-option value="Dr">Dr</mat-option>
                  </mat-select>
                  @if (individualDetailsForm.get('title')?.invalid) {
                    <mat-error>
                      Title is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Initials</mat-label>
                  <input required data-testid="individual-initials" formControlName="initials"
                    matInput>
                  @if (individualDetailsForm.get('initials')?.invalid) {
                    <mat-error>
                      Initials are invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Is the individual an animal?</mat-label>
                  <mat-select required data-testid="individual-is-animal"
                    formControlName="isAnimal">
                    <mat-option [value]="false">No</mat-option>
                    <mat-option [value]="true">Yes</mat-option>
                  </mat-select>
                  @if (individualDetailsForm.get('isAnimal')?.invalid) {
                    <mat-error>
                      Invalid selection
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Gender</mat-label>
                  <mat-select required data-testid="individual-gender" formControlName="gender">
                    <mat-option value="male">Male</mat-option>
                    <mat-option value="female">Female</mat-option>
                    <mat-option value="other">Other</mat-option>
                  </mat-select>
                  @if (individualDetailsForm.get('gender')?.invalid) {
                    <mat-error>
                      Gender is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Date of birth</mat-label>
                  <input required data-testid="individual-date-of-birth"
                    formControlName="dateOfBirth" matInput [matDatepicker]="picker1">
                  <mat-datepicker-toggle matSuffix [for]="picker1"></mat-datepicker-toggle>
                  <mat-datepicker #picker1></mat-datepicker>
                  @if (individualDetailsForm.get('date')?.invalid) {
                    <mat-error>
                      Date of birth is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
            </div>
            <div class="col-md-6">
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Email address</mat-label>
                  <input required data-testid="individual-email" formControlName="email" matInput>
                  @if (individualDetailsForm.get('email')?.invalid) {
                    <mat-error>
                      Email address is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Alternative email address</mat-label>
                  <input data-testid="individual-alt-email" formControlName="emailAlt" matInput>
                  @if (individualDetailsForm.get('emailAlt')?.invalid) {
                    <mat-error>
                      Alternative email address is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Number of dependants</mat-label>
                  <input data-testid="individual-number-of-dependants" type="number" min="0"
                    formControlName="numberOfDependants" matInput>
                  @if (individualDetailsForm.get('numberOfDependants')?.invalid) {
                    <mat-error>
                      Number of dependants is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Individual type</mat-label>
                  <mat-select required data-testid="individual-type" [disabled]="isLoading"
                    #chosenValue formControlName="individualType">
                    @for (type of individualTypes; track type) {
                      <mat-option [value]="type.id">
                        {{type.type}}
                      </mat-option>
                    }
                  </mat-select>
                  @if (individualDetailsForm.get('individualType')?.invalid) {
                    <mat-error>
                      Individual type is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field required appearance="outline">
                  <mat-label>Phone number</mat-label>
                  <input required data-testid="individual-phone-number"
                    formControlName="phoneNumber" matInput>
                  @if (individualDetailsForm.get('phoneNumber')?.invalid) {
                    <mat-error>
                      Phone number is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Alternative phone number</mat-label>
                  <input data-testid="individual-alt-phone-number"
                    formControlName="phoneNumberAlt" matInput>
                  @if (individualDetailsForm.get('phoneNumberAlt')?.invalid) {
                    <mat-error>
                      Alternative phone number is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
            </div>
          </div>
          <div class="text-end">
            <button class="mx-1" data-testid="previoous-step" color="primary" mat-raised-button
              matStepperPrevious>
              <mat-icon>chevron_left</mat-icon> Back
            </button>
            <button data-testid="next-step" [disabled]="!individualDetailsForm.valid" class="mx-1"
              color="primary" mat-raised-button matStepperNext>
               Next<mat-icon iconPositionEnd>chevron_right</mat-icon>
            </button>
          </div>
        </form>
      </mat-step>
      <mat-step [stepControl]="prescriberDetailsForm" [editable]="isEditable">
        <ng-template matStepLabel>Prescriber details</ng-template>
        <form class="mt-4" [formGroup]="prescriberDetailsForm">
          <div class="row">
            <div class="col-md-6">
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>First name</mat-label>
                  <input required readonly (click)="openSearchPrescriberDialog()" data-testid="individual-first-name" formControlName="firstName"
                    matInput>
                  @if (prescriberDetailsForm.get('firstName')?.invalid) {
                    <mat-error>
                      First name is invalid
                    </mat-error>
                  }
                  @if (!selectedPrescriber) {
                    <button type="button"
                      (click)="openSearchPrescriberDialog()" mat-icon-button matSuffix
                      [attr.aria-label]="'Search for a prescriber'">
                      <mat-icon>search</mat-icon>
                    </button>
                  }
                  @if (selectedPrescriber) {
                    <button type="button" (click)="deselectPrescriber()"
                      mat-icon-button matSuffix [attr.aria-label]="'Remove prescriber'">
                      <mat-icon>cancel</mat-icon>
                    </button>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Last name</mat-label>
                  <input required data-testid="individual-last-name" formControlName="lastName"
                    matInput>
                  @if (prescriberDetailsForm.get('lastName')?.invalid) {
                    <mat-error>
                      Last name is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Title</mat-label>
                  <mat-select required data-testid="individual-title" formControlName="title">
                    <mat-option value="Mr">Mr</mat-option>
                    <mat-option value="Mrs">Mrs</mat-option>
                    <mat-option value="Miss">Miss</mat-option>
                    <mat-option value="Prof">Prof</mat-option>
                    <mat-option value="Dr">Dr</mat-option>
                  </mat-select>
                  @if (prescriberDetailsForm.get('title')?.invalid) {
                    <mat-error>
                      Title is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Initials</mat-label>
                  <input required data-testid="individual-initials" formControlName="initials"
                    matInput>
                  @if (prescriberDetailsForm.get('initials')?.invalid) {
                    <mat-error>
                      Initials are invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Gender</mat-label>
                  <mat-select required data-testid="individual-gender" formControlName="gender">
                    <mat-option value="male">Male</mat-option>
                    <mat-option value="female">Female</mat-option>
                    <mat-option value="other">Other</mat-option>
                  </mat-select>
                  @if (prescriberDetailsForm.get('gender')?.invalid) {
                    <mat-error>
                      Gender is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Date of birth</mat-label>
                  <input required data-testid="individual-date-of-birth"
                    formControlName="dateOfBirth" matInput [matDatepicker]="picker3">
                  <mat-datepicker-toggle matSuffix [for]="picker3"></mat-datepicker-toggle>
                  <mat-datepicker #picker3></mat-datepicker>
                  @if (prescriberDetailsForm.get('date')?.invalid) {
                    <mat-error>
                      Date of birth is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
            </div>
            <div class="col-md-6">
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Email address</mat-label>
                  <input required data-testid="individual-email" formControlName="email" matInput>
                  @if (prescriberDetailsForm.get('email')?.invalid) {
                    <mat-error>
                      Email address is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Alternative email address</mat-label>
                  <input data-testid="individual-alt-email" formControlName="emailAlt" matInput>
                  @if (prescriberDetailsForm.get('emailAlt')?.invalid) {
                    <mat-error>
                      Alternative email address is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Number of dependants</mat-label>
                  <input data-testid="individual-number-of-dependants" type="number" min="0"
                    formControlName="numberOfDependants" matInput>
                  @if (prescriberDetailsForm.get('numberOfDependants')?.invalid) {
                    <mat-error>
                      Number of dependants is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Individual type</mat-label>
                  <mat-select required data-testid="individual-type" [disabled]="isLoading"
                    #chosenValue formControlName="individualType">
                    @for (type of individualTypes; track type) {
                      <mat-option [value]="type.id">
                        {{type.type}}
                      </mat-option>
                    }
                  </mat-select>
                  @if (prescriberDetailsForm.get('individualType')?.invalid) {
                    <mat-error>
                      Individual type is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field required appearance="outline">
                  <mat-label>Phone number</mat-label>
                  <input required data-testid="individual-phone-number"
                    formControlName="phoneNumber" matInput>
                  @if (prescriberDetailsForm.get('phoneNumber')?.invalid) {
                    <mat-error>
                      Phone number is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
              <p>
                <mat-form-field appearance="outline">
                  <mat-label>Alternative phone number</mat-label>
                  <input data-testid="individual-alt-phone-number"
                    formControlName="phoneNumberAlt" matInput>
                  @if (prescriberDetailsForm.get('phoneNumberAlt')?.invalid) {
                    <mat-error>
                      Alternative phone number is invalid
                    </mat-error>
                  }
                </mat-form-field>
              </p>
            </div>
          </div>
          <div class="text-end">
            <button class="mx-1" data-testid="previoous-step" color="primary" mat-raised-button
              matStepperPrevious>
              <mat-icon>chevron_left</mat-icon> Back
            </button>
            <button data-testid="next-step" [disabled]="!prescriberDetailsForm.valid" class="mx-1"
              color="primary" mat-raised-button matStepperNext>
               Next<mat-icon iconPositionEnd>chevron_right</mat-icon>
            </button>
          </div>
        </form>
      </mat-step>
    }

    <mat-step #addressStep id="address-details" [stepControl]="addressDetailsForm" [editable]="isEditable">
      <ng-template matStepLabel>
        Delivery address
      </ng-template>
      <form [formGroup]="addressDetailsForm" class="row">
        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>Search for a country</mat-label>
            <input required [disabled]="isLoading" type="text"
              aria-label="Search for a country" matInput formControlName="country"
              [matAutocomplete]="auto">
            <mat-autocomplete autoActiveFirstOption #auto="matAutocomplete">
              @for (country of filteredCountriesForIndividual | async; track country) {
                <mat-option
                  [value]="country.name">
                  {{country.name}}
                </mat-option>
              }
            </mat-autocomplete>
            <mat-icon matPrefix>search</mat-icon>
            @if (individualDetailsForm.get('country')?.invalid) {
              <mat-error>
                Country is invalid
              </mat-error>
            }
          </mat-form-field>
        </p>

        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>Province</mat-label>
            <mat-select required data-testid="individual-province" formControlName="province">
              @for (province of provinces; track province) {
                <mat-option value="{{province.name}}">
                  {{province.name}}
                </mat-option>
              }
            </mat-select>
            @if (individualDetailsForm.get('province')?.invalid) {
              <mat-error>
                Province is invalid
              </mat-error>
            }
          </mat-form-field>
        </p>

        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input type="text" formControlName="city" matInput />
            @if (addressDetailsForm.get('city')?.invalid) {
              <mat-error>
                City is invalid
              </mat-error>
            }
          </mat-form-field>
        </p>

        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>Address line 1</mat-label>
            <input required data-testid="individual-address-line-1" formControlName="addressLine1"
              matInput>
            @if (individualDetailsForm.get('addressLine1')?.invalid) {
              <mat-error>
                Address Line 1 is invalid
              </mat-error>
            }
          </mat-form-field>
        </p>

        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>Suburb</mat-label>
            <input data-testid="individual-address-line-2" formControlName="addressLine2" matInput>
            @if (individualDetailsForm.get('addressLine2')?.invalid) {
              <mat-error>
                Suburb is invalid
              </mat-error>
            }
          </mat-form-field>
        </p>

        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>Building Number</mat-label>
            <input required data-testid="individual-building-apt-number"
              formControlName="buildingAptNumber" type="number" min="1" matInput>
            @if (individualDetailsForm.get('buildingAptNumber')?.invalid) {
              <mat-error>
                Building Number is required
              </mat-error>
            }
          </mat-form-field>
        </p>

        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>Postcode</mat-label>
            <input required data-testid="individual-postcode" formControlName="postcode" matInput>
            @if (individualDetailsForm.get('postcode')?.invalid) {
              <mat-error>
                Postcode is invalid
              </mat-error>
            }
          </mat-form-field>
        </p>

        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>Select method of delivery</mat-label>
            <mat-select required data-testid="individual-delivery-type" #chosenValue
              formControlName="deliveryType">
              @for (type of deliveryTypes; track type) {
                <mat-option [value]="type.id">
                  {{type.type}}
                </mat-option>
              }
            </mat-select>
            @if (individualDetailsForm.get('deliveryType')?.invalid) {
              <mat-error>
                Choose from provided options
              </mat-error>
            }
          </mat-form-field>
        </p>

      </form>
      <div class="text-end">
        <button class="mx-1" data-testid="previoous-step" color="primary" mat-raised-button
          matStepperPrevious>
          <mat-icon>chevron_left</mat-icon> Back
        </button>
        <button data-testid="next-step" [disabled]="!addressDetailsForm.valid" class="mx-1" color="primary"
          mat-raised-button matStepperNext>
           Next<mat-icon iconPositionEnd>chevron_right</mat-icon>
        </button>
      </div>
    </mat-step>

    <mat-step [editable]="isEditable">
      <ng-template matStepLabel>Additional details</ng-template>
      <form [formGroup]="additionalDetailsForm" class="row">
        <p class="col-md-6">
          <mat-form-field appearance="outline">
            <mat-label>Customer Order Number</mat-label>
            <input formControlName="customerOrderNumber" matInput>
          </mat-form-field>
        </p>
      </form>
      <button class="mx-1" data-testid="previoous-step" color="primary" mat-raised-button
        matStepperPrevious>
        <mat-icon>chevron_left</mat-icon> Back
      </button>
      <button data-testid="next-step" class="mx-1" color="primary"
        mat-raised-button matStepperNext>
         Next<mat-icon iconPositionEnd>chevron_right</mat-icon>
      </button>

    </mat-step>

    <mat-step #addMedicineStep id="add-medicine" [editable]="isEditable">
      <ng-template matStepLabel>Add medicines</ng-template>
      <div class="prescription-step prescription-step--medicines">
        <core-add-medicine
          [orderItemStatuses]="orderItemStatuses"
          (onMedicineAdd)="addOrderItem($event)"
        ></core-add-medicine>
        <core-order-item-list
          (onDelete)="removeOrderItem($event)"
          [orderItems]="orderItems"
          [orderItemStatuses]="orderItemStatuses"
          [batchStatuses]="batchStatuses">
        </core-order-item-list>
        <div class="prescription-step__actions prescription-step__actions--medicines">
          <button class="mx-1" data-testid="previoous-step" color="primary" mat-raised-button matStepperPrevious>
            <mat-icon>chevron_left</mat-icon> Back
          </button>
          <button
            [disabled]="!canSavePrescription()"
            class="mx-1"
            color="primary"
            data-testid="save-button"
            mat-raised-button
            (click)="submitPrescription()">
            <mat-icon>save</mat-icon> Save
          </button>
        </div>
      </div>
    </mat-step>

    <mat-step [editable]="isEditable">
      <ng-template matStepLabel>Complete prescription</ng-template>
      @if (order) {
        <core-file-uploader [accept]="acceptFiles" [uploadButtonText]="'Complete prescription'"
        (onFileUploaded)="onFileUploaded($event)" [orderId]="order.id"></core-file-uploader>
      }
    </mat-step>
  </mat-stepper>
  </mat-card>
</div>
</div>